import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { catchError, from, switchMap, throwError } from 'rxjs';

/**
 * Re-reads the error body of a failed `responseType: 'blob'` request as JSON.
 *
 * Angular parses a response body according to the request's `responseType` for
 * **every** status, so a `blob` request that fails carries its error body as a
 * `Blob` — even when the server sent `application/json`. Anything upstream that
 * inspects `error.error` therefore reads properties off a `Blob` and gets
 * `undefined`.
 *
 * The one that matters is `@pozi/authmodule`'s `AuthInterceptor`: it only
 * refreshes the token when `error.error?.errorCode === 'TOKEN_EXPIRED'`, so an
 * expired token made table exports fail outright while every JSON request
 * recovered (TODO-28).
 *
 * **Order is load-bearing.** This must sit *after* every interceptor that reads
 * an error body in `provideHttpClient`'s interceptor list — the chain is built
 * with `reduceRight`, so a later entry is nearer the backend and normalises the
 * error before the earlier ones see it. In particular it must stay downstream
 * of `withInterceptorsFromDi()`, which is where the auth interceptor lives.
 *
 * Non-blob requests are passed straight through, and a body that is not JSON
 * (an HTML error page, say) is left exactly as it was.
 */
export const blobErrorInterceptor: HttpInterceptorFn = (req, next) => {
  if (req.responseType !== 'blob') {
    return next(req);
  }

  return next(req).pipe(
    catchError((error: unknown) => {
      if (
        !(error instanceof HttpErrorResponse) ||
        !(error.error instanceof Blob)
      ) {
        return throwError(() => error);
      }

      return from(error.error.text()).pipe(
        switchMap((text) => throwError(() => withParsedBody(error, text)))
      );
    })
  );
};

/**
 * Returns a copy of `error` carrying the parsed body, or `error` itself when
 * the body does not parse as JSON.
 */
function withParsedBody(
  error: HttpErrorResponse,
  text: string
): HttpErrorResponse {
  let parsed: unknown;

  try {
    parsed = JSON.parse(text);
  } catch {
    return error;
  }

  return new HttpErrorResponse({
    error: parsed,
    headers: error.headers,
    status: error.status,
    statusText: error.statusText,
    url: error.url ?? undefined,
  });
}
